// Crafting component for Alpine.js
import api from '../utils/api.js';
import craftingService from './crafting.service.js';

export function crafting() {
  return {
    // Data
    recipes: [],
    loading: false,
    error: null,
    isCrafting: false,
    craftResult: null,

    // Initialize component
    async init() {
      // Only load if authenticated
      if (!this.$store.app?.auth?.token) {
        console.log('⏳ Crafting: Not authenticated, skipping initialization');
        return;
      }
      
      // Don't load immediately - wait for crafting tab to be activated
      this.$watch('$store.app.ui.activeTab', (tab) => {
        if (tab === 'crafting' && !this._recipesLoaded) {
          console.log('🔨 Crafting tab activated, loading recipes...');
          this._recipesLoaded = true;
          this.loadRecipes();
        }
      });
      
      // If crafting tab is already active, load immediately
      if (this.$store.app.ui.activeTab === 'crafting') {
        this._recipesLoaded = true;
        await this.loadRecipes();
      }
    },

    // Load recipes from API
    async loadRecipes() {
      try {
        this.loading = true;
        this.error = null;
        
        const recipes = await craftingService.loadRecipes();
        this.recipes = recipes.map(recipe => ({
          ...recipe,
          craftTimes: 1 // Default quantity
        }));
        
      } catch (error) {
        console.error('Error loading recipes:', error);
        this.error = error.message || 'Error al cargar recetas';
      } finally {
        this.loading = false;
      }
    },

    // Get player's quantity of an item
    getPlayerItemQty(itemId) {
      const playerItem = this.$store.app.p().inventory?.find(item => item.itemId === itemId);
      return playerItem ? playerItem.qty : 0;
    },

    // Check if player has enough of an ingredient
    hasEnoughIngredient(ingredient) {
      const playerQty = this.getPlayerItemQty(ingredient.itemId);
      return playerQty >= ingredient.qty;
    },

    // Check if recipe can be crafted
    canCraftRecipe(recipe) {
      if (!this.$store.app?.hasPlayer?.()) return false;
      const player = this.$store.app.p();
      
      // Check level requirement
      if (player.level < recipe.levelReq) {
        return false;
      }
      
      // Check ingredients for multiple craft times
      const times = recipe.craftTimes || 1;
      for (const ingredient of recipe.ingredients) {
        const required = ingredient.qty * times;
        const available = this.getPlayerItemQty(ingredient.itemId);
        if (available < required) {
          return false;
        }
      }
      
      return true;
    },

    // Get craft issues for tooltip/display
    getCraftIssues(recipe) {
      if (!this.$store.app?.hasPlayer?.()) return ['Login requerido'];
      const player = this.$store.app.p();
      const issues = [];
      const times = recipe.craftTimes || 1;
      
      // Level check
      if (player.level < recipe.levelReq) {
        issues.push(`Requiere nivel ${recipe.levelReq} (tienes nivel ${player.level})`);
      }
      
      // Ingredient checks
      for (const ingredient of recipe.ingredients) {
        const required = ingredient.qty * times;
        const available = this.getPlayerItemQty(ingredient.itemId);
        if (available < required) {
          const missing = required - available;
          issues.push(`Necesitas ${missing} más de ${ingredient.item.name}`);
        }
      }
      
      return issues;
    },

    // Craft an item
    async craftItem(recipe) {
      if (!this.canCraftRecipe(recipe)) {
        this.$store.app.showNotification('No se puede craftear este item', 'error');
        return;
      }

      try {
        this.isCrafting = true;
        this.craftResult = null;
        
        const times = recipe.craftTimes || 1;
        const result = await craftingService.craft(recipe.id, times);
        
        if (result.success) {
          // Show success message
          this.craftResult = result;
          
          // Update inventory in store
          if (result.inventoryDelta) {
            await this.$store.app.refreshInventory();
          }
          
          // Auto-hide toast after 5 seconds
          setTimeout(() => {
            this.craftResult = null;
          }, 5000);
          
          this.$store.app.showNotification(`¡Has crafteado ${result.totalCrafted}x ${result.resultItem.name}!`, 'success');
        } else {
          this.$store.app.showNotification(result.error || 'Error al craftear', 'error');
        }
        
      } catch (error) {
        console.error('Error crafting item:', error);
        this.$store.app.showNotification('Error al craftear: ' + error.message, 'error');
      } finally {
        this.isCrafting = false;
      }
    },

    // Refresh recipes (called when tab becomes active)
    async refresh() {
      if (this.recipes.length === 0) {
        await this.loadRecipes();
      }
    }
  };
}